import React from 'react';
import {
    Page,
    SkeletonBodyText,
    Card,
    Banner,
    Link,
    Autocomplete,
    Icon,
    FormLayout,
    Select
} from '@shopify/polaris';
    import {
    SearchMinor
} from '@shopify/polaris-icons';
import OperationPreviewComponent from '../../components/OperationPreviewComponent';
import TagViewComponent from '../../components/TagViewComponent';
import { GenieTag } from '../../components/GenieTag';
import { Helmet } from 'react-helmet';
import { apiFetch as fetch, ResourceTypes } from '../../common/Helpers';

class ScenarioMergePage extends React.Component {

    state = {
        step: null,
        resourceType: "Product",
        isLoading: true,
        tagsToMerge: [],
        targetTag: null,
        preview: null,
        inputValue: '',
        inputMatchingTags: [],
    };

    render() {

        if (this.state.step == null) {
            return (
                <div>
                    <Helmet>
                        <title>Tag Genie - Merge Tags</title>
                    </Helmet>
                    <Page title="Merge Tags">
                        <SkeletonBodyText lines="20" />
                    </Page>
                </div>)
        }

        let hasTagsToMerge = this.state.tagsToMerge.length > 0 && this.state.targetTag;
        let resourcePlural = "products";
        let resourceSingular = "product";

        if (this.state.resourceType === "Customer") {
            resourcePlural = "customers";
            resourceSingular = "customer";
        }

        if (this.state.resourceType === "Order") {
            resourcePlural = "orders";
            resourceSingular = "order";
        }

        return (
            <div>
                <Helmet>
                    <title>Tag Genie - Merge Tags</title>
                </Helmet>
                {this.state.step === "create" &&
                    <Page title="Merge Tags">
                        <Card primaryFooterAction={{ content: 'Preview Merge Tags', disabled: !hasTagsToMerge || this.state.isLoading, onAction: this.handlePreview, loading: this.state.isLoading }}>
                            <Card.Section>
                                <Banner>
                                    <p>If you require more advanced functionality, consider using a <Link url="/operations/new/custom">Custom Tag Operation</Link>.
                                    </p>
                                </Banner>
                            </Card.Section>
                            <Card.Section title="Resource type">
                                <Select options={ResourceTypes} value={this.state.resourceType} onChange={this.onResourceChange} />
                            </Card.Section>
                            <Card.Section title="Store tags">
                                <TagViewComponent
                                    showMarkUnmark={true}
                                    canSelect={true}
                                    tags={this.getActiveTags()}
                                    selectedTags={this.state.tagsToMerge}
                                    selectedTagsState="removed"
                                    onSelectedChange={this.handleSelectedTagsChange}
                                    tagActionText="Click on a tag to select it for being merged." />
                            </Card.Section>
                            <Card.Section title="Target tag">
                                <FormLayout>
                                    <Autocomplete
                                        label="Target Tag"
                                        options={this.state.inputMatchingTags}
                                        selected={[this.state.targetTag]}
                                        onSelect={this.handleAutocompleteSelect}
                                        textField={<Autocomplete.TextField
                                            onChange={this.updateText}
                                            value={this.state.inputValue}
                                            prefix={<Icon source={SearchMinor} color="inkLighter" />}
                                            placeholder="Select target tag"
                                        />}
                                    />
                                </FormLayout>
                                <p>
                                    <br></br>
                                    <span>Target tag:&nbsp;
                                        {this.state.targetTag && <GenieTag state="added">{this.state.targetTag}</GenieTag>}
                                        {!this.state.targetTag && 'Not selected'}
                                    </span>
                                </p>
                            </Card.Section>
                        </Card>
                    </Page>
                }

                {this.state.step === "preview" &&
                    <Page title="Merge Tags - Preview">
                        <Card
                            title="Preview tag operation"
                            primaryFooterAction={{ content: "Execute Merge Tags", disabled: !this.state.preview.canExecute || this.state.isLoading, onAction: this.handleSubmit, loading: this.state.isLoading }}
                            secondaryFooterActions={[{ content: 'Back', disabled: this.state.isLoading, onAction: this.handleBack }]}>
                            <OperationPreviewComponent
                                preview={this.state.preview}
                                resourcePlural={resourcePlural}
                                resourceSingular={resourceSingular} />
                        </Card>
                    </Page>}
            </div>
        );
    }
    
    handleAutocompleteSelect = (selected) => {
        if (selected.length <= 0) {
            this.setState({
                inputValue: '',
                targetTag: null
            });
            return;
        }

        this.setState({
            inputValue: selected[0],
            targetTag: selected[0]
        });
    }

    getActiveTags = () => {
        if (this.state.resourceType === "Product") {
            return this.state.data.tags;
        }

        if (this.state.resourceType === "Customer") {
            return this.state.data.customerTags;
        }

        if (this.state.resourceType === "Order") {
            return this.state.data.orderTags;
        }

        return [];
    }

    onResourceChange = (value) => {
        this.setState({
            resourceType: value,
        }, () => this.updateText(''));
    }

    updateText = (value) => {
        this.setState({
            inputValue: value,
            targetTag: value
        });

        if (value === '') {
            this.setState({
                inputMatchingTags: this.getActiveTags().map((item) => {
                    return {
                        label: item.tagName,
                        value: item.tagName,
                    }
                }
                )
            });
            return;
        }

        const filterRegex = new RegExp(value, 'i');
        const resultOptions = this.getActiveTags().filter((option) => {
            return option.tagName.match(filterRegex)
        });

        this.setState({
            inputMatchingTags: resultOptions.map((item) => {
                return {
                    label: item.tagName,
                    value: item.tagName,
                }
            }
            )
        });
    }

    handleSelectedTagsChange = (newSelectedTags) => {
        this.setState({
            tagsToMerge: newSelectedTags
        })
    }

    fetchDashboardData = () => {
        fetch('api/dashboard')
            .then(response => { return response.json() })
            .then(data => {
                this.setState({
                    data: data,
                    step: "create",
                    isLoading: false,
                }, () => { this.updateText('') });
            });
    }

    handlePreview = () => {

        this.setState({
            isLoading: true
        });

        fetch(`api/operations/tag/scenario/merge/preview`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                tagsToMerge: this.state.tagsToMerge,
                targetTag: this.state.targetTag,
                resourceType: this.state.resourceType
            })
        })
            .then(response => response.json())
            .then(data => {
                this.setState({
                    isLoading: false,
                    step: "preview",
                    preview: data
                });
                window.scrollTo(0, 0);
            });
    }

    handleSubmit = () => {
        this.setState({
            isLoading: true
        });

        fetch(`api/operations/tag/scenario/merge`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                tagsToMerge: this.state.tagsToMerge,
                targetTag: this.state.targetTag,
                resourceType: this.state.resourceType
            })
        }).then(_ => {
            this.props.history.push("/operations");
        });
    }

    handleBack = async () => {
        this.setState({
            step: "create"
        })
    }

    componentDidMount() {
        this.fetchDashboardData();
    }
}

export default ScenarioMergePage;